import { useMemo, useState, useEffect } from "react";
import {
  Search,
  Calendar,
  ShoppingBag,
  Store,
  ShieldCheck,
  FileText,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import DataTable, { TableColumn } from "react-data-table-component";
import { cx } from "../../../utils";
import {
  useRevenueTable,
  RowItem,
  STATUS_OPTIONS,
} from "../../../hooks/useRevenueTable";

const tableStyles = {
  light: {
    headRow: {
      style: {
        backgroundColor: "#f9fafb",
        borderBottomWidth: "1px",
        borderBottomColor: "#e5e7eb",
        minHeight: "44px",
      },
    },
    headCells: {
      style: {
        fontSize: "0.75rem",
        fontWeight: "600",
        textTransform: "uppercase" as const,
        letterSpacing: "0.05em",
        color: "#6b7280",
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
      },
    },
    rows: {
      style: { fontSize: "0.875rem", color: "#1f2937", minHeight: "52px" },
      highlightOnHoverStyle: {
        backgroundColor: "#f3f4f6",
        borderBottomColor: "#e5e7eb",
        outline: "none",
      },
    },
    cells: {
      style: { paddingLeft: "1.5rem", paddingRight: "1.5rem" },
    },
    pagination: {
      style: {
        borderTopWidth: "1px",
        borderTopColor: "#e5e7eb",
        fontSize: "0.875rem",
        color: "#374151",
      },
    },
  },
  dark: {
    headRow: {
      style: {
        backgroundColor: "#374151",
        borderBottomWidth: "1px",
        borderBottomColor: "#4b5563",
        minHeight: "44px",
      },
    },
    headCells: {
      style: {
        fontSize: "0.75rem",
        fontWeight: "600",
        textTransform: "uppercase" as const,
        letterSpacing: "0.05em",
        color: "#d1d5db",
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
      },
    },
    rows: {
      style: {
        fontSize: "0.875rem",
        backgroundColor: "#1f2937",
        color: "#e5e7eb",
        minHeight: "52px",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#374151",
        borderBottomColor: "#4b5563",
        outline: "none",
      },
    },
    cells: {
      style: { paddingLeft: "1.5rem", paddingRight: "1.5rem" },
    },
    pagination: {
      style: {
        backgroundColor: "#1f2937",
        color: "#d1d5db",
        borderTopWidth: "1px",
        borderTopColor: "#4b5563",
        fontSize: "0.875rem",
      },
    },
  },
};

function FilterLabel({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
      {icon}
      {text}
    </label>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
      <p className="text-sm text-gray-500">Loading data...</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-16 h-16 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3">
        <FileText className="w-8 h-8 text-gray-300" />
      </div>
      <p className="text-sm text-gray-500">There are no records to display</p>
    </div>
  );
}

export default function RevenueTable() {
  const {
    mallOptions,
    selectedMall,
    isMallDisabled,
    tenantOptions,
    selectedTenant,
    isTenantDisabled,
    selectedStatus,
    setSelectedStatus,
    startDate,
    endDate,
    setSearchQuery,
    handleMallChange,
    handleTenantChange,
    handleDateChange,
    handlePageChange,
    handleRowsPerPageChange,
    filteredData,
    isLoading,
    totalAmount,
    currentPage,
    rowsPerPage,
    formatDate,
    formatAmount,
  } = useRevenueTable();

  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(inputValue);
    }, 200);
    return () => clearTimeout(handler);
  }, [inputValue, setSearchQuery]);

  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const hasDateSelected = Boolean(startDate);

  const columns: TableColumn<RowItem>[] = useMemo(
    () => [
      {
        name: "No",
        width: "90px",
        cell: (_row, index) => (
          <span className="text-gray-500 dark:text-gray-400">
            {(currentPage - 1) * rowsPerPage + index + 1}
          </span>
        ),
      },
      {
        name: "Transaction Number",
        selector: (row) => row.TransactionNumber,
        sortable: true,
        cell: (row) => (
          <span className="font-medium text-gray-800 dark:text-gray-200">
            {row.TransactionNumber}
          </span>
        ),
      },
      {
        name: "Date",
        selector: (row) => row.TransactionDate,
        sortable: true,
        cell: (row) => (
          <span className="text-gray-800 dark:text-gray-200">
            {formatDate(row.TransactionDate)}
          </span>
        ),
      },
      {
        name: "Amount (Rp)",
        selector: (row) => row.Amount,
        sortable: true,
        right: true,
        cell: (row) => (
          <span className="text-gray-800 dark:text-gray-200">
            {formatAmount(row.Amount)}
          </span>
        ),
      },
      {
        name: "Remarks",
        selector: (row) => row.Remarks ?? "",
        sortable: true,
        cell: (row) => (
          <span className="text-gray-500 dark:text-gray-400">
            {row.Remarks ?? "-"}
          </span>
        ),
      },
    ],
    [currentPage, rowsPerPage, formatDate, formatAmount]
  );

  const selectClass = (disabled: boolean) =>
    cx(
      "w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm bg-white text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-white",
      disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
    );

  const onExportPDF = () => {
    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Transaction Report", 14, 15);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Generated: ${new Date().toLocaleDateString("en-GB")}`, 14, 22);

      const tableData = filteredData.map((row, idx) => [
        idx + 1,
        row.TransactionNumber,
        formatDate(row.TransactionDate),
        formatAmount(row.Amount),
        row.Remarks ?? "-",
      ]);

      autoTable(doc, {
        head: [
          [
            "No",
            "Transaction Number",
            "Date",
            "Amount (Rp)",
            "Remarks",
          ],
        ],
        body: tableData,
        startY: 28,
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 15, halign: "center" },
          1: { cellWidth: 50 },
          2: { cellWidth: 40 },
          3: { cellWidth: 40, halign: "right" },
          4: { cellWidth: 35 },
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        margin: { top: 28, left: 14, right: 14 },
      });

      const fileName = `Transaction_Report_${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(fileName);

      Swal.fire({
        icon: "success",
        title: "PDF Export Success",
        text: `File ${fileName} has been downloaded`,
        timer: 2000,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "PDF Export Failed",
        text: String(error),
      });
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl space-y-4">
        <div className="bg-white rounded-lg shadow dark:bg-gray-800">
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <FilterLabel
                  icon={<ShoppingBag className="w-4 h-4 text-indigo-600" />}
                  text="Mall"
                />
                <select
                  value={selectedMall}
                  disabled={isMallDisabled}
                  onChange={(e) => handleMallChange(e.target.value)}
                  className={selectClass(isMallDisabled)}
                >
                  <option value="">-- Choose One --</option>
                  {mallOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <FilterLabel
                  icon={<Store className="w-4 h-4 text-indigo-600" />}
                  text="Store"
                />
                <select
                  value={selectedTenant}
                  disabled={isTenantDisabled}
                  onChange={(e) => handleTenantChange(e.target.value)}
                  className={selectClass(isTenantDisabled)}
                >
                  <option value="">-- Choose One --</option>
                  {tenantOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <FilterLabel
                  icon={<Calendar className="w-4 h-4 text-indigo-600" />}
                  text="Date"
                />
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10 hidden sm:block" />
                  <DatePicker
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={handleDateChange}
                    dateFormat="dd MMM yyyy"
                    placeholderText="Select date range"
                    popperPlacement="bottom-start"
                    popperProps={{ strategy: "fixed" }}
                    className="w-full sm:pl-9 pl-3 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700 text-sm outline-none dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    wrapperClassName="w-full"
                    calendarClassName="shadow-xl border-0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <FilterLabel
                  icon={<ShieldCheck className="w-4 h-4 text-indigo-600" />}
                  text="Status"
                />
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className={selectClass(false)}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {hasDateSelected ? (
          <div className="bg-white rounded-lg shadow overflow-hidden dark:bg-gray-800">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-b border-gray-100 dark:border-gray-700">
              <div>
                {!isLoading && filteredData.length > 0 && (
                  <button
                    onClick={onExportPDF}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-200 shadow-sm"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.92,12.31 10.92,12.31Z" />
                    </svg>
                    PDF
                  </button>
                )}
              </div>
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                />
              </div>
            </div>

            <div className="hidden sm:block">
              <DataTable<RowItem>
                columns={columns}
                data={filteredData}
                progressPending={isLoading}
                progressComponent={<LoadingState />}
                noDataComponent={<EmptyState />}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                highlightOnHover
                responsive
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
                customStyles={isDark ? tableStyles.dark : tableStyles.light}
              />

              {!isLoading && filteredData.length > 0 && (
                <div className="flex justify-between items-center px-6 py-3 bg-gray-50 dark:bg-gray-700 border-t-2 border-gray-200 dark:border-gray-600 text-sm font-bold text-gray-700 dark:text-gray-200">
                  <span>Total ({filteredData.length} records)</span>
                  <span>Rp {formatAmount(totalAmount)}</span>
                </div>
              )}
            </div>

            <div className="block sm:hidden p-4">
              {isLoading ? (
                <LoadingState />
              ) : filteredData.length === 0 ? (
                <EmptyState />
              ) : (
                <>
                  <div className="flex flex-col gap-3">
                    {filteredData.map((row, i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4 flex flex-col gap-2 shadow-sm"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                            #{i + 1}
                          </span>
                          <span
                            className={cx(
                              "text-xs px-2 py-0.5 rounded-full font-medium",
                              selectedStatus === "valid"
                                ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                                : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                            )}
                          >
                            {selectedStatus === "valid" ? "Valid" : "Void"}
                          </span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">
                              Trx No.
                            </span>
                            <span className="text-gray-800 dark:text-gray-100 font-medium break-all">
                              {row.TransactionNumber}
                            </span>
                          </div>
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">
                              Date
                            </span>
                            <span className="text-gray-800 dark:text-gray-100 font-medium">
                              {formatDate(row.TransactionDate)}
                            </span>
                          </div>
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">
                              Amount
                            </span>
                            <span className="text-gray-800 dark:text-gray-100 font-medium">
                              Rp {formatAmount(row.Amount)}
                            </span>
                          </div>
                          {row.Remarks && (
                            <div className="flex gap-2 text-sm">
                              <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">
                                Remarks
                              </span>
                              <span className="text-gray-600 dark:text-gray-400">
                                {row.Remarks}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between text-sm font-bold text-gray-700 dark:text-gray-200">
                      <span>Total ({filteredData.length} records)</span>
                      <span>Rp {formatAmount(totalAmount)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col items-center justify-center py-12 sm:py-16 gap-3">
            <div className="w-16 h-16 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <Calendar className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Please select a date range to view data
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
